import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { gameRepo } from "../../repo/game-repo.ts";
import AdminPageTitle from "../admin-pages/admin-page-title.tsx";
import { AppContext } from "../../context/app-context.tsx";
import SeasonWinnerFormFields, {
  emptySeasonWinnerForm,
  isSeasonWinnerFormValid,
  toSeasonWinnerPayload,
} from "./season-winner-form-fields.tsx";

export default function EditSeasonWinner() {
  const navigate = useNavigate();
  const { id } = useParams();
  const winnerId = Number(id);
  const { setMessage } = useContext(AppContext);
  const [form, setForm] = useState(emptySeasonWinnerForm);
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!winnerId) {
      setMessage({ type: "error", message: "شناسه برنده معتبر نیست." });
      navigate("/admin/season-winners");
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        const response = await gameRepo.getSeasonWinner(winnerId);
        if (!response.status || !response.data) {
          setMessage({ type: "error", message: response.message || "دریافت برنده ناموفق بود." });
          navigate("/admin/season-winners");
          return;
        }
        const data = response.data;
        setUsername(data.username ?? "");
        setForm({
          season_number: String(data.season_number ?? ""),
          user_id: String(data.user_id ?? ""),
          rank: String(data.rank ?? ""),
          level: String(data.level ?? ""),
          score: String(data.score ?? ""),
          prize: data.prize ?? "",
        });
      } catch {
        setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
        navigate("/admin/season-winners");
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [winnerId, navigate, setMessage]);

  const onChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isSeasonWinnerFormValid(form) || !winnerId) {
      setMessage({ type: "error", message: "لطفاً همه فیلدها را کامل کنید." });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await gameRepo.updateSeasonWinner(winnerId, toSeasonWinnerPayload(form));
      if (!response.status) {
        setMessage({ type: "error", message: response.message || "ویرایش برنده ناموفق بود." });
        return;
      }

      setMessage({ type: "success", message: "برنده با موفقیت ویرایش شد." });
      navigate("/admin/season-winners");
    } catch {
      setMessage({ type: "error", message: "خطا در ارتباط با سرور. دوباره تلاش کنید." });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="space-y-4">
        <AdminPageTitle title="ویرایش برنده" />
        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 text-slate-300">در حال بارگذاری...</div>
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <AdminPageTitle title="ویرایش برنده" />
      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
          <SeasonWinnerFormFields form={form} username={username} onChange={onChange} />
          <div className="flex items-center gap-3 md:col-span-2">
            <button
              type="submit"
              disabled={!isSeasonWinnerFormValid(form) || isSubmitting}
              className="rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "در حال ذخیره…" : "ذخیره تغییرات"}
            </button>
            <Link
              to="/admin/season-winners"
              className="inline-flex rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 transition hover:border-indigo-400 hover:text-indigo-300"
            >
              انصراف
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
}
